<template>
  <el-card>
    <el-alert
      v-if="$route.query.expired"
      class="text-left mb-4"
      title="Session Expired"
      type="error"
      :closable="false"
    >
      Your session has expired, please sign in again.
    </el-alert>

    <Avatar
      :email="email"
    />

    <SignInForm
      :email.sync="email"
      :password.sync="password"
      :sso.sync="sso"
      class="pt-3 pb-4"
      @submit="handleSignIn"
    />

    <section>
      <el-collapse
        v-model="ssoActive"
        accordion
      >
        <el-collapse-item name="1">
          <template slot="title">
            Single Sign On
            &nbsp;
            <FontAwesomeIcon
              :icon="['fab', 'windows']"
            />
          </template>
          <el-input
            v-model="sso"
            placeholder="Account Code"
            clearable
          />
        </el-collapse-item>
      </el-collapse>
    </section>

    <el-divider />

    <el-button
      type="primary"
      :disabled="isSigningIn || isLoadingApplication"
      :loading="isSigningIn || isLoadingApplication"
      class="w-100"
      @click.native="handleSignIn"
    >
      {{ signInButtonText }}
    </el-button>

    <el-divider />

    <section>
      <el-link
        type="primary"
        @click.native="$router.push({ name: 'authentication-forgotpassword' })"
      >
        Forgot your password or can't sign in?
      </el-link>
      <br>
      <el-link
        type="primary"
        href="https://tigfreight.com/start-shipping-with-tig"
        target="_blank"
      >
        Sign up for an account
      </el-link>
    </section>
    <MigrateDialog
      :open360-dialog-visible.sync="open360DialogVisible"
      :show-continue-to-open-freight="showContinueToOpenFreight"
      @continue-to-openfreight="continueToOpenFreight"
    />
  </el-card>
</template>

<script>
import Avatar from '@/components/Avatar'
import SignInForm from '@/components/Authentication/SignInForm'
import Request, { POST } from '@/plugins/sdk/Request'
import { ImmortalDB as Database } from 'immortal-db'
import MigrateDialog from '@/components/Authentication/MigrateToOpen360'
import Session from '@/plugins/session'

export default {
  components: {
    Avatar,
    SignInForm,
    MigrateDialog
  },
  data () {
    return {
      isSigningIn: false,
      isLoadingApplication: false,
      email: '',
      password: '',
      sso: '',
      ssoActive: false,
      open360DialogVisible: false,
      showContinueToOpenFreight: false
    }
  },
  computed: {
    signInButtonText () {
      return this.sso.length ? 'Sign In with SSO' : 'Sign In'
    }
  },
  created () {
    this.hasUsedSSO()

    if (this.$route.query.token) {
      this.isSigningIn = true
      this.handleSSO()
      this.isSigningIn = false
    }

    if (this.$route.query.email) {
      this.email = this.$route.query.email
    }
  },
  methods: {
    async handleSignIn () {
      this.isSigningIn = true

      if (this.sso.length) {
        await Database.set('sso', this.sso)

        const request = await (Object.assign(new Request(), {
          method: POST,
          _pathname: 'authenticate/sso/' + this.sso
        })).build()

        const response = await fetch(request)

        if (response.ok) {
          let res = await response.json()
          window.location.href = atob(res.url)
        }
      } else {
        try {
          const established = await this.$session.establish(this.email, this.password)

          if (!established) {
            throw new Error()
          } else {
            console.log('Checking if user can continue to OpenFreight')
            if (!await this.$session.active) {
              await this.$session.refresh()
            }
            let session = await this.$session.instance
            console.log(session.team.open360_only, session.profile.firebase_id)
            this.showContinueToOpenFreight = !session.team.open360_only && !session.profile.firebase_id
            this.open360DialogVisible = true
            return
          }
        } catch {
          this.$message.error('Invalid Email or Password provided')
        }
      }

      this.isSigningIn = false
    },
    async continueToOpenFreight () {
      let redirection = {}
      if ((await Session.instance).team.legacy.shipper) {
        // The session is active, check if the user is a legacy
        // "shipper" (customer). If they are, redirect them to
        // the legacy shipper route.
        redirection = {
          name: 'legacy',
          query: { tenancy: (await Session.instance).tenancy.id, token: await Session.token },
          replace: true
        }
        this
          .$router
          .replace(redirection)

        return
      }

      if (this.$route.query.redirect) {
        redirection.path = this.$route.query.redirect
      } else {
        redirection.name = 'application'
      }

      this
        .$router
        .replace(redirection)
    },
    goToOpen360 () {
      let url = 'https://auth.open360.com/login?app=ACCOUNT_APP&env=o360&redirection=ACCOUNT_MIGRATED&email=' + this.email
      window.open(url, '_blank')
    },
    async handleSSO () {
      try {
        let redirection = {}
        this.isLoadingApplication = true
        const established = await this.$session.tradeTokenForSession(this.$route.query.token)
        if (!established) {
          throw new Error()
        } else {
          if (this.$route.query.redirect) {
            redirection.path = this.$route.query.redirect
          } else {
            redirection.name = 'application'
          }
          this
            .$router
            .replace(redirection)
        }
      } catch {
        this.$message.error('Unable to authenticate with IDP')
      }
    },
    async hasUsedSSO () {
      let code = await Database.get('sso')
      if (code) {
        this.sso = code
        this.ssoActive = 1
      }
    }
  },
  beforeRouteLeave (to, from, next) {
    this.isLoadingApplication = true

    next()
  }
}
</script>
